<script setup lang="ts">
useSeoMeta({
  title: 'Mapping Where Quality Problems Are Created Across a System — QikSolve',
  description:
    'Trace a quality problem across people, process, information, and technology to look beyond where it was detected.',
});
</script>

<template>
  <section class="max-w-3xl mx-auto px-4 py-16 prose">
    <h1>Mapping Where Quality Problems Are Created Across a System</h1>
    <p>
      When a quality problem is found, it is tempting to fix it at the point where it was noticed. That
      response addresses the symptom, but it does not always address where the problem was created. A
      deviation raised on the shop floor may have originated in an unclear handover between teams, a
      decision right that was never assigned, or a step that assumed information which was not actually
      available at the time.
    </p>

    <h2>Why the point of detection is rarely the point of origin</h2>
    <p>
      Work moves through people, process, information, and technology in sequence. A gap introduced early
      in that sequence, such as an ambiguous responsibility or an assumption that no longer matches
      practice, often only becomes visible once it reaches a later, more scrutinised step. Treating the
      later step as the whole problem can lead to a fix that does not prevent the same gap recurring
      somewhere else.
    </p>

    <h2>A practical way to trace the path</h2>
    <p>
      Tracing where a quality problem was created does not require a large-scale audit. It can start with
      a small number of practical questions applied to one recurring issue:
    </p>
    <ul>
      <li>Where did the information or decision that led to this problem first enter the process?</li>
      <li>Who was responsible for that step, and was that responsibility clearly assigned?</li>
      <li>Did the system or procedure in place at that step reflect how work actually happens?</li>
      <li>What would have made the gap visible earlier, before it reached the point of detection?</li>
    </ul>
    <p>
      Answering these questions with the people who actually do the work, not only the people who review
      it afterwards, tends to surface where the real gap sits.
    </p>

    <h2>Use the pattern, not just the incident</h2>
    <p>
      A single incident review can miss a repeating pattern. Reviewing several related issues together
      often reveals that they share the same point of origin, even when the visible symptom looks
      different each time. That shared origin, not the individual incident, is the more useful place to
      make a proportionate change.
    </p>

    <h2>Where this leads next</h2>
    <p>
      Once the point of origin is understood, the next practical question is usually how to improve the
      surrounding flow without weakening the controls that are already working. That question is covered
      separately in
      <NuxtLink
        to="/product/improving-flow-without-weakening-control"
        class="text-blue-600 hover:underline"
      >
        improving flow without weakening control
      </NuxtLink>.
    </p>
    <p>
      This page is part of
      <NuxtLink
        to="/product/systems-thinking-for-regulated-operations"
        class="text-blue-600 hover:underline"
      >
        systems thinking for regulated operations
      </NuxtLink>, which explains how people, process, information, technology, decisions, and feedback
      work together across a regulated operation.
    </p>

    <h2>Discuss where your quality problems are created</h2>
    <p>
      If the same type of quality issue keeps recurring in different forms, a discovery conversation can
      help trace it back to its actual point of origin.
    </p>
    <p class="font-semibold">
      <NuxtLink
        to="/contact"
        class="text-blue-600 hover:underline"
      >
        Book a discovery call
      </NuxtLink>
      to discuss a recurring quality issue in your operation.
    </p>
  </section>
</template>
